'use strict';

/* ══════════════════════════════════════════════════════════════
   personalization.js — unified personalisation drawer
   Replaces engraving.js / inscription.js / custom-text.js.

   One drawer can hold 1–2 independent options (e.g. Engraving AND a
   Monogram on the same ring — see personalization-block.liquid /
   personalization-option.liquid). Each option is checkbox-driven and
   always optional on its own: leaving it unchecked (or unchecking +
   Apply) just skips that option, same as if it didn't exist.
   ══════════════════════════════════════════════════════════════ */
(function () {
  var LETTERS_ONLY_PATTERN = /[^A-Za-z ]/g;

  function uid() {
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function initPersonalization(block) {
    var sid          = block.dataset.sectionId;
    var drawer       = document.querySelector('[data-personalization-drawer="' + sid + '"]');
    var overlay      = document.querySelector('[data-personalization-overlay="' + sid + '"]');
    var closeBtn     = document.querySelector('[data-personalization-close="' + sid + '"]');
    var trigger      = block.querySelector('[data-personalization-trigger]');
    var applyBtn     = document.querySelector('[data-personalization-apply="' + sid + '"]');
    var btnLabel     = block.querySelector('[data-personalization-btn-label]');
    var optionEls    = Array.from(drawer ? drawer.querySelectorAll('[data-personalization-option]') : []);
    var originalBtnText = btnLabel ? btnLabel.textContent : '';

    if (!trigger || !drawer || !overlay || !optionEls.length) return;

    document.body.appendChild(overlay);
    document.body.appendChild(drawer);

    // Build a descriptor per option, wiring up its own checkbox/input/
    // counter/error and reading its own config + hidden form inputs.
    var options = optionEls.map(function (el) {
      var key         = el.dataset.optionKey;
      var toggle      = el.querySelector('[data-personalization-toggle]');
      var panel       = el.querySelector('[data-personalization-panel]');
      var textInput   = el.querySelector('[data-personalization-text]');
      var counter     = el.querySelector('[data-personalization-counter]');
      var errorEl     = el.querySelector('[data-personalization-error]');
      var formInput   = document.querySelector('[data-personalization-form-input="' + key + '"]');
      var linkInput   = document.querySelector('[data-personalization-link-input="' + key + '"]');
      var maxChars    = textInput ? parseInt(textInput.getAttribute('maxlength'), 10) : 20;

      var opt = {
        key: key,
        label: el.dataset.label || key,
        feeVariantId: el.dataset.feeVariantId,
        price: el.dataset.price || '',
        isFree: el.dataset.isFree === 'true',
        lettersOnly: el.dataset.lettersOnly === 'true',
        isRing: el.dataset.isRing === 'true',
        toggle: toggle,
        panel: panel,
        textInput: textInput,
        counter: counter,
        errorEl: errorEl,
        formInput: formInput,
        linkInput: linkInput,
        maxChars: maxChars,
        confirmed: false
      };

      function setChecked(checked) {
        if (toggle) toggle.checked = checked;
        if (textInput) textInput.disabled = !checked;
        if (panel) {
          panel.setAttribute('aria-hidden', String(!checked));
          // Reveal is driven by a CSS grid-template-rows transition keyed
          // off [aria-hidden] — belt-and-suspenders: also force it via
          // inline style directly, so the field can never silently fail to
          // appear (stale/cached CSS, a specificity conflict from another
          // stylesheet, etc.) even if that attribute-selector rule doesn't
          // apply for some reason. Doesn't fight the CSS transition; it's
          // setting the exact same end state, just guaranteed.
          panel.style.gridTemplateRows = checked ? '1fr' : '0fr';
        }
        if (toggle) toggle.setAttribute('aria-expanded', String(checked));
        if (checked) {
          setTimeout(function () { if (textInput) textInput.focus(); }, 320);
        }
      }

      if (toggle) {
        toggle.addEventListener('change', function () {
          setChecked(toggle.checked);
          if (errorEl) errorEl.hidden = true;
        });
      }

      if (textInput) {
        textInput.addEventListener('input', function () {
          var raw = textInput.value;
          var val = raw;
          if (opt.lettersOnly && LETTERS_ONLY_PATTERN.test(raw)) {
            val = raw.replace(LETTERS_ONLY_PATTERN, '');
            textInput.value = val;
            if (errorEl) {
              errorEl.textContent = 'Letters only — no numbers or punctuation.';
              errorEl.hidden = false;
            }
          } else if (errorEl) {
            errorEl.hidden = true;
          }
          var len = val.length;
          if (counter) {
            counter.textContent = len + '/' + maxChars;
            counter.classList.toggle('is-near-limit', len >= maxChars - 3);
          }
        });
      }

      return opt;
    });

    /* ── Open / close ── */
    function openDrawer() {
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('personalization-open');
      if (window.__lenis) window.__lenis.stop();
      document.addEventListener('keydown', onEsc);
    }

    function closeDrawer() {
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('personalization-open');
      if (window.__lenis) window.__lenis.start();
      document.removeEventListener('keydown', onEsc);
    }

    function onEsc(e) {
      if (e.key === 'Escape') closeDrawer();
    }

    trigger.addEventListener('click', openDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    overlay.addEventListener('click', closeDrawer);

    function updateTriggerLabel() {
      var confirmedOpts = options.filter(function (o) { return o.confirmed; });
      if (!confirmedOpts.length) {
        if (btnLabel) btnLabel.textContent = originalBtnText;
        return;
      }
      if (confirmedOpts.length > 1) {
        if (btnLabel) btnLabel.textContent = 'Personalised — Edit';
        return;
      }
      var text = confirmedOpts[0].formInput ? confirmedOpts[0].formInput.value : '';
      var display = text.length > 22 ? text.substring(0, 22) + '…' : text;
      if (btnLabel) btnLabel.textContent = '"' + display + '" — Edit';
    }

    /* ── Apply: commits each option's checkbox+text state independently ── */
    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        // Validate every checked option BEFORE committing any of them, so
        // one missing field doesn't leave the others half-applied.
        for (var i = 0; i < options.length; i++) {
          var o = options[i];
          var checked = o.toggle ? o.toggle.checked : false;
          if (!checked) continue;
          var text = o.textInput ? o.textInput.value.trim() : '';
          if (!text) {
            if (o.errorEl) { o.errorEl.textContent = 'Please enter your text, or switch the toggle off.'; o.errorEl.hidden = false; }
            if (o.textInput) o.textInput.focus();
            return;
          }
        }

        options.forEach(function (o) {
          var checked = o.toggle ? o.toggle.checked : false;
          if (!checked) {
            if (o.formInput) { o.formInput.value = ''; o.formInput.disabled = true; }
            if (o.textInput)   o.textInput.value = '';
            if (o.counter)     o.counter.textContent = '0/' + o.maxChars;
            o.confirmed = false;
            return;
          }
          var text = o.textInput ? o.textInput.value.trim() : '';
          if (o.formInput) { o.formInput.value = text; o.formInput.disabled = false; }
          o.confirmed = true;
        });

        updateTriggerLabel();
        closeDrawer();
      });
    }

    var formInputWithProductForm = options.map(function (o) { return o.formInput; }).filter(function (fi) {
      return fi && fi.dataset.productFormId;
    })[0];
    var formId = formInputWithProductForm ? formInputWithProductForm.dataset.productFormId : null;
    var form   = formId ? document.getElementById(formId) : null;

    // Any option with no separate fee line (isFree) already submits its
    // formInput as a real name+form field on the product's own form —
    // nothing further needed for those. Only paid options (with a fee
    // line) need submit interception below.
    var paidOptions = options.filter(function (o) { return !o.isFree; });
    if (!paidOptions.length) return;

    if (!form) {
      if (window.Shopify && window.Shopify.designMode) {
        console.warn('[personalization] could not find the product form (data-product-form-id) — paid options cannot be enforced on this page.');
      }
      return;
    }

    // ── PAID options ───────────────────────────────────────────────────
    // Each confirmed paid option gets its own separate fee-product cart
    // line (Shopify has no built-in way to surcharge a line without
    // Shopify Functions/Plus), carrying the product's title, the ring size
    // (when this is a ring — so two of the same ring in different sizes,
    // only one personalised, stay distinguishable on the order) and the
    // confirmed text as line item properties.
    //
    // Sequencing is deliberately product-first, fees-second: let the
    // product itself be added via the normal AJAX flow and WAIT for its
    // own success/failure signal before adding any fee — each tagged with
    // a freshly generated, one-off link value also submitted as a property
    // on the product's own line (a distinct property per option — see
    // personalization-block.liquid — since a form can't submit two fields
    // under the same property name and have both survive). That shared
    // value is what lets cart.js remove the fee automatically if the
    // product line is later removed, and guarantees every fee line's
    // properties are unique.
    var resubmittingAfterFee = false;
    var awaitingProductResult = false;
    var unsubUpdate = null;
    var unsubError = null;
    var awaitTimeout = null;

    function cleanupSubscriptions() {
      awaitingProductResult = false;
      if (unsubUpdate) { unsubUpdate(); unsubUpdate = null; }
      if (unsubError) { unsubError(); unsubError = null; }
      if (awaitTimeout) { clearTimeout(awaitTimeout); awaitTimeout = null; }
    }

    function addFeeForOption(o, linkValue) {
      var titleEl = document.querySelector('.product__title');
      var productTitle = titleEl ? titleEl.textContent.trim() : document.title;

      var properties = {
        'For': productTitle,
        'Text': o.formInput.value,
        '_Linked Item': linkValue
      };
      if (o.isRing) {
        var sizeDisplay = document.querySelector('[data-size-display]');
        var sizeText = sizeDisplay ? sizeDisplay.textContent.trim() : '';
        if (sizeText) properties['Ring Size'] = sizeText;
      }

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: parseInt(o.feeVariantId, 10),
          quantity: 1,
          properties: properties
        })
      }).then(function (res) {
        if (!res.ok) throw new Error('personalization fee add failed for ' + o.key + ': ' + res.status);
        return res.json();
      });
    }

    function addFeesForConfirmedProduct(confirmedPaidOptions) {
      Promise.all(confirmedPaidOptions.map(function (o) {
        return addFeeForOption(o, o.linkInput ? o.linkInput.value : uid());
      })).then(function () {
        // The product's own add already opened+rendered the drawer from ITS
        // OWN /cart/add.js response — that response predates these fees, so
        // the drawer wouldn't otherwise show them. Publishing cartUpdate
        // (source != 'cart-items') makes CartItems/CartDrawerItems' own
        // existing auto-refresh (see cart.js) re-fetch and re-render with
        // the fee line(s) now included.
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'personalization' });
      }).catch(function () {
        // The product IS already in the cart at this point — don't roll it
        // back. Surface it instead so the shopper knows to get in touch.
        var anyErrorEl = confirmedPaidOptions[0] && confirmedPaidOptions[0].errorEl;
        if (anyErrorEl) {
          anyErrorEl.textContent = 'Your item was added, but we couldn’t record your personalisation — please contact us before checkout.';
          anyErrorEl.hidden = false;
        }
        openDrawer();
      });
    }

    form.addEventListener('submit', function (e) {
      if (resubmittingAfterFee) {
        resubmittingAfterFee = false;
        return;
      }

      var confirmedPaidOptions = paidOptions.filter(function (o) {
        return o.confirmed && o.formInput && o.formInput.value;
      });

      // Nothing confirmed — always optional, let the submit proceed untouched.
      if (!confirmedPaidOptions.length) return;

      var missingFeeVariant = confirmedPaidOptions.find(function (o) { return !o.feeVariantId; });
      if (missingFeeVariant) {
        // Misconfigured (fee variant metafield/setting unset for this
        // option) — block rather than silently add a blank, unfulfillable
        // order for a personalisation that was requested but can't be
        // charged/recorded.
        e.preventDefault();
        e.stopPropagation();
        if (missingFeeVariant.errorEl) {
          missingFeeVariant.errorEl.textContent = 'This option is temporarily unavailable — please contact us to order with personalisation.';
          missingFeeVariant.errorEl.hidden = false;
        }
        openDrawer();
        if (window.Shopify && window.Shopify.designMode) {
          console.warn('[personalization] no fee variant configured for "' + missingFeeVariant.key + '" — add-to-cart is blocked until it is.');
        }
        return;
      }

      var sizeInput = document.querySelector('[data-size-property-input]');
      if (sizeInput && !sizeInput.value) {
        e.preventDefault();
        e.stopPropagation();
        var sizeTrigger = document.querySelector('[data-size-picker-trigger]');
        if (sizeTrigger) sizeTrigger.click();
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (awaitingProductResult) return; // already in flight
      awaitingProductResult = true;

      confirmedPaidOptions.forEach(function (o) {
        var linkValue = uid();
        if (o.linkInput) o.linkInput.value = linkValue;
      });

      var idInput = form.querySelector('input[name="id"]');
      var expectedVariantId = idInput ? idInput.value : null;

      unsubUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
        if (String(evt.productVariantId) !== String(expectedVariantId)) return;
        cleanupSubscriptions();
        addFeesForConfirmedProduct(confirmedPaidOptions);
      });
      unsubError = subscribe(PUB_SUB_EVENTS.cartError, function (evt) {
        if (String(evt.productVariantId) !== String(expectedVariantId)) return;
        cleanupSubscriptions();
      });
      awaitTimeout = setTimeout(function () {
        cleanupSubscriptions();
      }, 6000);

      resubmittingAfterFee = true;
      setTimeout(function () { form.requestSubmit(); }, 0);
    }, true);
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-personalization-block]').forEach(initPersonalization);
  });
})();
